import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import VisuallyHidden from 'components/VisuallyHidden'
import { hideEmail } from 'utils/user'
import { ACCENT, BORDER, INK, MUTED } from 'features/actions/workspace/ui'

const COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'appointedBy', label: 'Added by' },
  { key: 'appointedAt', label: 'Added on' },
]

const ADDED_BY_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'super_admin', label: 'By a Super Admin' },
  { key: 'bp', label: 'By Building Passport' },
]

const formatDate = iso => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '')

const addedByGroup = r => (r.appointedVia === 'super_admin' || r.appointedVia === 'invitation' ? 'super_admin' : 'bp')

const addedByText = r => r.appointedBy || (r.appointedVia === 'release' || r.appointedVia === 'automatic' ? 'Set up by Building Passport' : 'Building Passport support')

const SuperAdminsTable = ({ rows, onRemove }) => {
  const [q, setQ] = useState('')
  const [addedByFilter, setAddedByFilter] = useState('all')
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' })

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase()
    const filtered = rows
      .filter(r => addedByFilter === 'all' || addedByGroup(r) === addedByFilter)
      .filter(r => !term || r.name.toLowerCase().includes(term) || (hideEmail(r.email || null) || '').toLowerCase().includes(term))
    const value = r => (sort.key === 'appointedAt' ? new Date(r.appointedAt || 0).getTime() : sort.key === 'appointedBy' ? addedByText(r).toLowerCase() : String(r[sort.key] || '').toLowerCase())
    return [...filtered].sort((a, b) => (value(a) > value(b) ? 1 : value(a) < value(b) ? -1 : 0) * (sort.dir === 'asc' ? 1 : -1))
  }, [rows, q, addedByFilter, sort])

  const last = rows.length <= 1

  return (
    <Wrapper>
      <Controls>
        <Search aria-label='Search Super Admins' placeholder='Search by name or email' value={q} onChange={e => setQ(e.target.value)} />
        <Chips role='group' aria-label='Filter by how they were added'>
          {ADDED_BY_FILTERS.map(f => (
            <Chip key={f.key} type='button' aria-pressed={addedByFilter === f.key} $on={addedByFilter === f.key} onClick={() => setAddedByFilter(f.key)}>
              {f.label}
            </Chip>
          ))}
        </Chips>
      </Controls>
      <Count>Showing {visible.length} of {rows.length} Super Admins</Count>
      {last && <Hint>Your organisation needs at least one Super Admin.</Hint>}
      <TableScroll>
        <Table>
          <thead>
            <tr>
              {COLUMNS.map(c => (
                <th key={c.key} aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <SortButton type='button' onClick={() => setSort(s => ({ key: c.key, dir: s.key === c.key && s.dir === 'asc' ? 'desc' : 'asc' }))}>
                    {c.label} {sort.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
                  </SortButton>
                </th>
              ))}
              <th><VisuallyHidden>Actions</VisuallyHidden></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(r => (
              <tr key={r.userId}>
                <td>
                  <Name>{r.name}{r.isYou ? ' (you)' : ''}</Name>
                  <Email>{hideEmail(r.email || null)}</Email>
                </td>
                <td>{addedByText(r)}</td>
                <td>{formatDate(r.appointedAt)}</td>
                <td>
                  <RemoveButton type='button' aria-label={`Remove ${r.name}`} disabled={last} onClick={() => onRemove(r)}>Remove</RemoveButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
    </Wrapper>
  )
}

export default SuperAdminsTable

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
`

const Search = styled.input`
  flex: 1 1 260px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  font-size: 15px;
  font-family: inherit;
  max-width: 360px;

  &:focus { outline: 2px solid ${ACCENT}; outline-offset: 1px; }

  @media (max-width: 768px) {
    max-width: none;
    flex-basis: 100%;
  }
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Chip = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid ${({ $on }) => ($on ? ACCENT : BORDER)};
  background: ${({ $on }) => ($on ? '#fff' : '#fafafa')};
  color: ${({ $on }) => ($on ? INK : MUTED)};
  font: inherit;
  font-size: 14px;
  font-weight: ${({ $on }) => ($on ? 700 : 600)};
  cursor: pointer;
`

const Count = styled.div`
  font-size: 14px;
  color: ${MUTED};
`

const Hint = styled.div`
  font-size: 15px;
  color: ${INK};
  font-weight: 600;
`

const TableScroll = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  font-size: 15px;

  th, td { text-align: left; padding: 12px 10px; border-bottom: 1px solid ${BORDER}; vertical-align: top; }
`

const SortButton = styled.button`
  border: 0;
  background: transparent;
  font: inherit;
  font-weight: 700;
  font-size: 14px;
  color: ${MUTED};
  cursor: pointer;
  min-height: 40px;
  padding: 0;

  &:hover { color: ${INK}; }
`

const Name = styled.div`
  font-weight: 700;
  color: ${INK};
`

const Email = styled.div`
  font-size: 14px;
  color: ${MUTED};
`

const RemoveButton = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { cursor: not-allowed; color: #98a2b3; }
`
